"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  ChevronDown,
  Info,
  Loader2,
  Lock,
  MoreVertical,
  Save,
} from "lucide-react";
import { UsersLayout } from "@/components/pages/users-layout";
import { Card, Pill } from "@/components/pulse-shell";
import { api, ApiError, type Agent, type PermissionGroup, type PlatformRole } from "@/lib/api";
import { useAuthProfile } from "@/lib/auth-context";

const DEFAULT_OPEN = new Set(["Users & access", "Sessions", "UI chrome", "Finance"]);

export function UsersRoleEditPage() {
  const { roleId = "" } = useParams();
  const navigate = useNavigate();
  const me = useAuthProfile();
  const canManage = Boolean(me?.permissions?.includes("roles.manage"));

  const [role, setRole] = useState<PlatformRole | null>(null);
  const [groups, setGroups] = useState<PermissionGroup[]>([]);
  const [agentsCatalog, setAgentsCatalog] = useState<Agent[]>([]);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [allowedAgents, setAllowedAgents] = useState<Set<string>>(new Set());
  const [openGroups, setOpenGroups] = useState<Set<string>>(new Set(DEFAULT_OPEN));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [dirty, setDirty] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const isSystem = role?.type === "SYSTEM" || role?.systemImmutable;
  const readOnly = Boolean(isSystem) || !canManage;

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [detail, catalog, agentsRes] = await Promise.all([
        api.roles.get(roleId),
        api.roles.permissions(),
        api.listAgents().catch(() => ({ agents: [] as Agent[] })),
      ]);
      const r = detail.role;
      setRole(r);
      setName(r.name);
      setDescription(r.description || "");
      setSelected(new Set(r.permissions || []));
      setAllowedAgents(new Set(r.allowedAgentIds || []));
      setGroups(catalog.groups || r.permissionCatalog || []);
      setAgentsCatalog(
        (agentsRes.agents || [])
          .slice()
          .sort((a, b) => String(a.name || a.agentId).localeCompare(String(b.name || b.agentId))),
      );
      setDirty(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load role");
    } finally {
      setLoading(false);
    }
  }, [roleId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!dirty) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty]);

  const enabledByGroup = useMemo(() => {
    const map = new Map<string, number>();
    for (const g of groups) {
      let n = 0;
      for (const p of g.permissions) if (selected.has(p.key)) n += 1;
      map.set(g.name, n);
    }
    return map;
  }, [groups, selected]);

  function toggleGroup(nameKey: string) {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(nameKey)) next.delete(nameKey);
      else next.add(nameKey);
      return next;
    });
  }

  function expandAll() {
    setOpenGroups(new Set(groups.map((g) => g.name)));
  }

  function clearAll() {
    if (readOnly) return;
    setSelected(new Set());
    setDirty(true);
  }

  function togglePerm(key: string) {
    if (readOnly) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
    setDirty(true);
  }

  function toggleAgent(agentId: string) {
    if (readOnly) return;
    setAllowedAgents((prev) => {
      const next = new Set(prev);
      if (next.has(agentId)) next.delete(agentId);
      else next.add(agentId);
      return next;
    });
    setDirty(true);
  }

  function selectAllAgents() {
    if (readOnly) return;
    setAllowedAgents(new Set(agentsCatalog.map((a) => a.agentId).filter(Boolean)));
    setDirty(true);
  }

  function clearAgents() {
    if (readOnly) return;
    setAllowedAgents(new Set());
    setDirty(true);
  }

  async function save() {
    if (readOnly || !role) return;
    setSaving(true);
    setError("");
    try {
      const out = await api.roles.update(role.roleId, {
        name: name.trim(),
        description: description.trim(),
        permissions: [...selected],
        allowedAgentIds: [...allowedAgents],
        version: role.version,
      });
      setRole(out.role);
      setSelected(new Set(out.role.permissions || []));
      setAllowedAgents(new Set(out.role.allowedAgentIds || []));
      setDirty(false);
    } catch (e) {
      setError(e instanceof ApiError || e instanceof Error ? e.message : "Save failed");
    } finally {
      setSaving(false);
    }
  }

  function tryBack() {
    if (dirty && !window.confirm("Discard unsaved changes?")) return;
    navigate("/users/roles");
  }

  if (loading) {
    return (
      <UsersLayout>
        <div className="users-role-edit users-state">
          <Loader2 className="spin" size={20} /> Loading role…
        </div>
      </UsersLayout>
    );
  }

  if (!role) {
    return (
      <UsersLayout>
        <div className="users-role-edit">
          <p className="users-error">{error || "Role not found"}</p>
          <Link to="/users/roles">Back to Roles & Permissions</Link>
        </div>
      </UsersLayout>
    );
  }

  return (
    <UsersLayout>
    <div className="users-role-edit">
      <button type="button" className="users-back" onClick={tryBack}>
        <ArrowLeft size={16} /> Roles & Permissions
      </button>

      <div className="users-role-edit-head">
        <div>
          <div className="users-role-edit-title-row">
            <h1>{readOnly ? "View role" : "Edit role"}</h1>
            <Pill tone={isSystem ? "gray" : "purple"}>
              {isSystem ? "System role" : "Custom role"}
            </Pill>
            {isSystem && <Lock size={16} aria-label="System role" />}
          </div>
          <p className="users-role-edit-meta">
            {name || role.name}
            {" • "}
            {role.userCount ?? 0} assigned user{(role.userCount ?? 0) === 1 ? "" : "s"}
          </p>
        </div>
        <div className="users-role-edit-actions">
          <button type="button" className="btn ghost" onClick={tryBack}>
            Cancel
          </button>
          {!readOnly && (
            <button
              type="button"
              className="btn primary"
              disabled={saving || !dirty}
              onClick={() => void save()}
            >
              <Save size={16} /> {saving ? "Saving…" : "Save changes"}
            </button>
          )}
          {!isSystem && canManage && (
            <div className="users-menu-wrap">
              <button
                type="button"
                className="icon-button"
                aria-label="Role actions"
                onClick={() => setMenuOpen((o) => !o)}
              >
                <MoreVertical size={16} />
              </button>
              {menuOpen && (
                <div className="users-menu" role="menu">
                  <button
                    type="button"
                    role="menuitem"
                    className="danger"
                    onClick={async () => {
                      if (!window.confirm("Delete this custom role?")) return;
                      try {
                        await api.roles.remove(role.roleId);
                        navigate("/users/roles");
                      } catch (e) {
                        setError(e instanceof Error ? e.message : "Delete failed");
                      }
                    }}
                  >
                    Delete role
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="users-error" role="alert">
          {error}
        </div>
      )}

      <div className="users-info-banner">
        <Info size={16} />
        <span>
          {readOnly
            ? "System roles are read-only. Permissions shown are enforced for all assigned users."
            : "Saved changes apply immediately to all users assigned this role."}
        </span>
      </div>

      <div className="users-role-edit-grid">
        <Card title="Role details">
          <label className="users-modal-full">
            Role name
            <input
              value={name}
              disabled={readOnly}
              onChange={(e) => {
                setName(e.target.value);
                setDirty(true);
              }}
            />
          </label>
          <label className="users-modal-full">
            Description
            <textarea
              rows={4}
              value={description}
              disabled={readOnly}
              onChange={(e) => {
                setDescription(e.target.value);
                setDirty(true);
              }}
            />
          </label>
        </Card>

        <Card
          title="Allowed agents"
          action={
            !readOnly ? (
              <div className="perm-toolbar">
                <button type="button" className="btn ghost sm" onClick={selectAllAgents}>
                  Select all
                </button>
                <button type="button" className="btn ghost sm" onClick={clearAgents}>
                  Clear
                </button>
              </div>
            ) : undefined
          }
        >
          <p className="muted" style={{ marginBottom: 12 }}>
            Empty means no agents. Users with this role can only start or list sessions for selected agents.
            {isSystem ? " System roles are kept in sync when agents are created." : null}
          </p>
          {agentsCatalog.length === 0 ? (
            <p className="muted">No agents available to assign.</p>
          ) : (
            <div className="perm-group-body">
              {agentsCatalog.map((a) => (
                <label key={a.agentId} className="perm-row">
                  <input
                    type="checkbox"
                    checked={allowedAgents.has(a.agentId)}
                    disabled={readOnly}
                    onChange={() => toggleAgent(a.agentId)}
                  />
                  <span>
                    <b>{a.name || a.agentId}</b>
                    <span className="muted">{a.agentId}{a.role ? ` · ${a.role}` : ""}</span>
                  </span>
                </label>
              ))}
            </div>
          )}
          <p className="muted" style={{ marginTop: 8 }}>
            {allowedAgents.size} selected
          </p>
        </Card>

        <Card
          title="Permissions"
          action={
            <div className="perm-toolbar">
              <button type="button" className="btn ghost sm" onClick={expandAll}>
                Expand all
              </button>
              {!readOnly && (
                <button type="button" className="btn ghost sm" onClick={clearAll}>
                  Clear all
                </button>
              )}
            </div>
          }
        >
          {groups.map((g) => {
            const open = openGroups.has(g.name);
            const enabled = enabledByGroup.get(g.name) || 0;
            return (
              <div key={g.name} className="perm-group">
                <button
                  type="button"
                  className="perm-group-head"
                  aria-expanded={open}
                  onClick={() => toggleGroup(g.name)}
                >
                  <ChevronDown
                    size={16}
                    className={open ? "open" : ""}
                    aria-hidden
                  />
                  <span>{g.name}</span>
                  <span className="muted">
                    {enabled}/{g.permissions.length} enabled
                  </span>
                </button>
                {open && (
                  <div className="perm-group-body">
                    {g.permissions.map((p) => (
                      <label key={p.key} className="perm-row">
                        <input
                          type="checkbox"
                          checked={selected.has(p.key)}
                          disabled={readOnly}
                          onChange={() => togglePerm(p.key)}
                        />
                        <span>
                          <b>{p.label}</b>
                          <span className="muted">{p.description}</span>
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </Card>
      </div>
    </div>
    </UsersLayout>
  );
}
